import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Keyboard, RotateCcw, Search, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import {
  loadShortcuts,
  saveShortcuts,
  formatKeyCode,
  findConflicts,
  DEFAULT_SHORTCUTS,
  SHORTCUT_GROUPS,
  ACTION_LABEL_KEYS,
} from "@/utils/keyboardShortcuts";

export const ShortcutsSettingsCard = () => {
  const { t } = useI18n();
  const [shortcuts, setShortcutsState] = useState(loadShortcuts());
  const [recordingFor, setRecordingFor] = useState(null);
  const [filter, setFilter] = useState("");

  // Listen for keypress when recording a shortcut
  useEffect(() => {
    if (!recordingFor) return undefined;
    const onKey = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.code === "Tab") return;
      if (e.code === "Escape" && recordingFor !== "escape") {
        // Escape cancels recording (unless we're recording the escape action)
        setRecordingFor(null);
        return;
      }
      const next = { ...shortcuts, [recordingFor]: e.code };
      setShortcutsState(next);
      saveShortcuts(next);
      setRecordingFor(null);
    };
    window.addEventListener("keydown", onKey, { capture: true });
    return () => window.removeEventListener("keydown", onKey, { capture: true });
  }, [recordingFor, shortcuts]);

  const resetAll = () => {
    if (!window.confirm(t("shortcuts.confirmResetAll"))) return;
    setShortcutsState({ ...DEFAULT_SHORTCUTS });
    saveShortcuts({ ...DEFAULT_SHORTCUTS });
    toast.success(t("shortcuts.resetDone"));
  };

  const clearOne = (action) => {
    const next = { ...shortcuts, [action]: "" };
    setShortcutsState(next);
    saveShortcuts(next);
  };

  const conflicts = findConflicts(shortcuts);
  const filterLower = filter.trim().toLowerCase();

  return (
    <Card
      className="p-6 mb-6 glass"
      data-testid="shortcuts-settings-card"
    >
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <div className="flex items-center gap-3">
          <Keyboard className="text-primary" size={28} />
          <h3 className="text-3xl font-bold text-foreground">
            {t("shortcuts.title")}
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={resetAll}
          data-testid="shortcuts-reset-all-btn"
        >
          <RotateCcw size={14} className="mr-2" />
          {t("shortcuts.resetAll")}
        </Button>
      </div>
      <p className="text-sm text-muted-foreground mb-4">
        {t("shortcuts.description")}
      </p>

      {/* Filter */}
      <div className="relative mb-4">
        <Search
          size={16}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
        />
        <Input
          placeholder={t("shortcuts.filterPlaceholder")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="pl-9"
          data-testid="shortcuts-filter-input"
        />
      </div>

      {Object.keys(conflicts).length > 0 && (
        <div
          className="mb-4 p-3 rounded-lg flex items-center gap-2 text-sm"
          style={{
            background: "rgba(234, 179, 8, 0.1)",
            border: "1px solid rgba(234, 179, 8, 0.4)",
            color: "#a16207",
          }}
          data-testid="shortcuts-conflicts-warning"
        >
          <AlertTriangle size={16} />
          <span>{t("shortcuts.conflictsWarning")}</span>
        </div>
      )}

      {SHORTCUT_GROUPS.map((group) => {
        const visibleActions = group.actions.filter((action) => {
          if (!filterLower) return true;
          const labelKey = ACTION_LABEL_KEYS[action];
          const label = (labelKey ? t(labelKey) : action).toLowerCase();
          const code = (shortcuts[action] || "").toLowerCase();
          return label.includes(filterLower) || code.includes(filterLower);
        });
        if (visibleActions.length === 0) return null;
        return (
          <div
            key={group.id}
            className="mb-5"
            data-testid={`shortcuts-group-${group.id}`}
          >
            <h4 className="text-lg font-bold text-foreground mb-3 uppercase tracking-wider opacity-80">
              {t(group.titleKey)}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {visibleActions.map((action) => {
                const labelKey = ACTION_LABEL_KEYS[action];
                const label = labelKey ? t(labelKey) : action;
                const code = shortcuts[action];
                const isRecording = recordingFor === action;
                const inConflict = !!conflicts[action];
                return (
                  <div
                    key={action}
                    className="flex items-center justify-between bg-background border border-border rounded-lg p-3"
                    style={
                      inConflict
                        ? { borderColor: "#eab308", background: "rgba(234,179,8,0.05)" }
                        : undefined
                    }
                  >
                    <span className="text-foreground font-medium flex-1 mr-3">
                      {label}
                    </span>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant={isRecording ? "destructive" : "outline"}
                        onClick={() =>
                          setRecordingFor(isRecording ? null : action)
                        }
                        className="min-w-24 font-mono"
                        data-testid={`shortcut-${action}`}
                      >
                        {isRecording
                          ? t("shortcuts.pressKey")
                          : formatKeyCode(code)}
                      </Button>
                      {code && !isRecording && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => clearOne(action)}
                          title={t("shortcuts.clear")}
                          data-testid={`shortcut-${action}-clear`}
                          className="text-muted-foreground hover:text-destructive px-2"
                        >
                          ✕
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      <div className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
        {t("shortcuts.localStorageNote")}
      </div>
    </Card>
  );
};

export default ShortcutsSettingsCard;
